import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import pollTask from '@afosto/utils-react/tasks/pollTask';
import ContentDrawer from '@afosto/app-components-react/ContentDrawer';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import { flows as flowsApi } from '@afosto/content/api/Flows';
import translations from './translations';

const DataFlowDrawer = ({ open, filter, entityId, onClose, onFinish }) => {
  const intl = useIntl();
  const [flows, setFlows] = useState([]);
  const [isTriggeringDataFlow, setIsTriggeringDataFlow] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    let isMounted = true;

    const fetchFlows = async () => {
      try {
        const response = await flowsApi.listDataFlows({
          headers: {
            'x-page-size': 250,
          },
        });
        const { data } = response;

        const filteredFlows = filter ? data.filter(flow => flow?.source === filter) : data;

        if (isMounted) {
          setFlows(sortArrayByProperty(filteredFlows, 'name'));
        }
      } catch (e) {
        setFlows([]);
      }
    };

    fetchFlows();

    return () => {
      isMounted = false;
    };
  }, [filter]);

  const handleTriggerDataFlow = id => async () => {
    try {
      setIsTriggeringDataFlow(true);
      const response = await flowsApi.triggerFlow(id, { entity_id: entityId });
      const { headers } = response;
      const taskID = headers?.['x-task-id'];

      await pollTask(taskID, 10);

      enqueueSnackbar(intl.formatMessage(translations.feedback.dataFlowFinished), {
        variant: 'success',
      });

      if (onFinish && typeof onFinish === 'function') {
        onFinish();
      }
    } catch (error) {
      if (error?.response?.status === 400) {
        enqueueSnackbar(intl.formatMessage(translations.feedback.dataFlowUnmatched), {
          variant: 'error',
        });
        return;
      }

      const errorMessage =
        error?.result && typeof error?.result === 'string'
          ? error.result
          : intl.formatMessage(translations.feedback.dataFlowFailed);

      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
    } finally {
      setIsTriggeringDataFlow(false);
    }
  };

  return (
    <ContentDrawer
      isOpen={open}
      handleClose={onClose}
      drawerWidth="420px"
      title={intl.formatMessage(translations.title)}
    >
      <LoadingWrapper isLoading={isTriggeringDataFlow}>
        {flows.length > 0 && (
          <List>
            {flows.map(flow => (
              <ListItem key={flow.id} button onClick={handleTriggerDataFlow(flow.id)}>
                <ListItemText primary={flow.name} />
              </ListItem>
            ))}
          </List>
        )}
        {!flows.length && (
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage {...translations.feedback.noDataflows} />
          </Typography>
        )}
      </LoadingWrapper>
    </ContentDrawer>
  );
};

DataFlowDrawer.propTypes = {
  entityId: PropTypes.string,
  filter: PropTypes.string,
  onClose: PropTypes.func,
  onFinish: PropTypes.func,
  open: PropTypes.bool,
};

DataFlowDrawer.defaultProps = {
  entityId: null,
  filter: null,
  onClose: undefined,
  onFinish: undefined,
  open: false,
};

export default DataFlowDrawer;
